import React, { useState, useEffect, useRef } from 'react';
import { fileAPI } from '../lib/api';

const FileEditModal = ({ file, isOpen, onClose, onSuccess }) => {
	const [formData, setFormData] = useState({
		description: '',
		fileCategory: 'GENERAL',
		tags: ''
	});
	const [searchTerm, setSearchTerm] = useState('');
	const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const categoryDropdownRef = useRef(null);

	const categories = [
		// Pitch & Presentation
		{ value: 'PITCH_DECK', label: 'Pitch Deck', group: 'Pitch & Presentation' },
		{ value: 'INVESTOR_PRESENTATION', label: 'Investor Presentation', group: 'Pitch & Presentation' },
		{ value: 'EXECUTIVE_SUMMARY', label: 'Executive Summary', group: 'Pitch & Presentation' },
		
		// Financial Documents
		{ value: 'FINANCIAL', label: 'Financial Statements', group: 'Financial Documents' },
		{ value: 'AUDITED_FINANCIALS', label: 'Audited Financials', group: 'Financial Documents' },
		{ value: 'PROJECTED_FINANCIALS', label: 'Projected Financials', group: 'Financial Documents' },
		{ value: 'BALANCE_SHEET', label: 'Balance Sheet', group: 'Financial Documents' },
		{ value: 'P_L_STATEMENT', label: 'P&L Statement', group: 'Financial Documents' },
		{ value: 'CASH_FLOW_STATEMENT', label: 'Cash Flow Statement', group: 'Financial Documents' },
		{ value: 'VALUATION_CERTIFICATE', label: 'Valuation Certificate', group: 'Financial Documents' },
		{ value: 'CAP_TABLE', label: 'Cap Table', group: 'Financial Documents' },
		
		// Legal Documents
		{ value: 'LEGAL', label: 'Legal Documents', group: 'Legal Documents' },
		{ value: 'INCORPORATION', label: 'Incorporation Documents', group: 'Legal Documents' },
		{ value: 'AGREEMENT', label: 'Agreements', group: 'Legal Documents' },
		{ value: 'SHA', label: 'Shareholder Agreement (SHA)', group: 'Legal Documents' },
		{ value: 'TERM_SHEET', label: 'Term Sheet', group: 'Legal Documents' },
		{ value: 'CCD', label: 'Compulsory Convertible Debentures (CCD)', group: 'Legal Documents' },
		{ value: 'SAFE_AGREEMENT', label: 'SAFE Agreement', group: 'Legal Documents' },
		{ value: 'CONVERTIBLE_NOTE', label: 'Convertible Note', group: 'Legal Documents' },
		{ value: 'EMPLOYMENT_AGREEMENT', label: 'Employment Agreement', group: 'Legal Documents' },
		{ value: 'NDA', label: 'Non-Disclosure Agreement (NDA)', group: 'Legal Documents' },
		{ value: 'IP_DOCUMENTS', label: 'IP Documents', group: 'Legal Documents' },
		{ value: 'PATENTS', label: 'Patents', group: 'Legal Documents' },
		{ value: 'TRADEMARKS', label: 'Trademarks', group: 'Legal Documents' },
		
		// Business Documents
		{ value: 'BUSINESS_PLAN', label: 'Business Plan', group: 'Business Documents' },
		{ value: 'MARKET_RESEARCH', label: 'Market Research', group: 'Business Documents' },
		{ value: 'COMPETITIVE_ANALYSIS', label: 'Competitive Analysis', group: 'Business Documents' },
		{ value: 'BUSINESS_MODEL', label: 'Business Model', group: 'Business Documents' },
		{ value: 'GO_TO_MARKET', label: 'Go-to-Market Strategy', group: 'Business Documents' },
		{ value: 'PRODUCT_DOCUMENTATION', label: 'Product Documentation', group: 'Business Documents' },
		{ value: 'TECHNICAL_SPECS', label: 'Technical Specifications', group: 'Business Documents' },
		
		// Compliance & Regulatory
		{ value: 'COMPLIANCE', label: 'Compliance Documents', group: 'Compliance & Regulatory' },
		{ value: 'LICENSES', label: 'Licenses & Permits', group: 'Compliance & Regulatory' },
		{ value: 'REGULATORY_FILINGS', label: 'Regulatory Filings', group: 'Compliance & Regulatory' },
		{ value: 'TAX_DOCUMENTS', label: 'Tax Documents', group: 'Compliance & Regulatory' },
		
		// Operations
		{ value: 'OPERATIONAL', label: 'Operational Documents', group: 'Operations' },
		{ value: 'HR_DOCUMENTS', label: 'HR Documents', group: 'Operations' },
		{ value: 'POLICIES', label: 'Company Policies', group: 'Operations' },
		{ value: 'PROCESSES', label: 'Process Documents', group: 'Operations' },
		
		// Marketing & Sales
		{ value: 'MARKETING_MATERIALS', label: 'Marketing Materials', group: 'Marketing & Sales' },
		{ value: 'SALES_DECK', label: 'Sales Deck', group: 'Marketing & Sales' },
		{ value: 'BRAND_GUIDELINES', label: 'Brand Guidelines', group: 'Marketing & Sales' },
		
		// Other
		{ value: 'GENERAL', label: 'General Documents', group: 'Other' },
		{ value: 'OTHER', label: 'Other', group: 'Other' }
	];

	// Group categories
	const groupedCategories = categories.reduce((acc, cat) => {
		const group = cat.group || 'Other';
		if (!acc[group]) {
			acc[group] = [];
		}
		acc[group].push(cat);
		return acc;
	}, {});

	// Filter categories based on search term
	const filteredCategories = searchTerm
		? categories.filter(cat =>
			cat.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
			cat.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
			cat.group.toLowerCase().includes(searchTerm.toLowerCase())
		)
		: categories;

	// Group filtered categories
	const filteredGroupedCategories = filteredCategories.reduce((acc, cat) => {
		const group = cat.group || 'Other';
		if (!acc[group]) {
			acc[group] = [];
		}
		acc[group].push(cat);
		return acc;
	}, {});

	// Get selected category label
	const selectedCategoryLabel = categories.find(cat => cat.value === formData.fileCategory)?.label || 'Select Document Type';

	useEffect(() => {
		if (file && isOpen) {
			// Parse tags from tagList (comma-separated string)
			const existingTags = file.tagList 
				? file.tagList.split(',').filter(tag => tag.trim() !== file.fileCategory && tag.trim() !== file.documentType).join(', ')
				: '';
			
			setFormData({
				description: file.description || '',
				fileCategory: file.fileCategory || file.documentType || 'GENERAL',
				tags: existingTags
			});
			setError(null);
		}
	}, [file, isOpen]);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
				setShowCategoryDropdown(false);
			}
		};

		if (showCategoryDropdown) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [showCategoryDropdown]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError(null);

		try {
			await fileAPI.updateFile(file.id, {
				description: formData.description,
				fileCategory: formData.fileCategory,
				tags: formData.tags
			});

			onSuccess && onSuccess();
			onClose();
		} catch (err) {
			setError(err.response?.data?.message || err.message || 'Failed to update file');
		} finally {
			setLoading(false);
		}
	};

	if (!isOpen || !file) return null;

	return (
		<div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-75 flex items-center justify-center p-4">
			<div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
				<div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
					<div className="flex justify-between items-center">
						<h2 className="text-xl font-semibold text-gray-900">
							Edit File: {file.originalName || file.filename}
						</h2>
						<button
							onClick={onClose}
							className="text-gray-400 hover:text-gray-500"
						>
							<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
					</div>
				</div>

				<form onSubmit={handleSubmit} className="p-6 space-y-6">
					{error && (
						<div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
							{error}
						</div>
					)}

					{/* Document Type */}
					<div className="relative" ref={categoryDropdownRef}>
						<label className="block text-sm font-medium text-gray-700 mb-2">
							Document Type <span className="text-red-500">*</span>
						</label>
						
						<div className="relative">
							<div
								onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
								className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer flex items-center justify-between"
							>
								<span className={formData.fileCategory ? 'text-gray-900' : 'text-gray-400'}>
									{selectedCategoryLabel}
								</span>
								<svg
									className={`w-5 h-5 text-gray-400 transition-transform ${showCategoryDropdown ? 'transform rotate-180' : ''}`}
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
								</svg>
							</div>

							{showCategoryDropdown && (
								<div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden">
									<div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
										<div className="relative">
											<input
												type="text"
												value={searchTerm}
												onChange={(e) => setSearchTerm(e.target.value)}
												onClick={(e) => e.stopPropagation()}
												placeholder="Search document type..."
												className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
												autoFocus
											/>
											<svg
												className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
											</svg>
										</div>
									</div>

									<div className="overflow-y-auto max-h-64">
										{Object.keys(filteredGroupedCategories).length === 0 ? (
											<div className="px-4 py-3 text-sm text-gray-500 text-center">
												No document types found
											</div>
										) : (
											Object.entries(filteredGroupedCategories).map(([group, groupCats]) => (
												<div key={group}>
													<div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-700 uppercase tracking-wide sticky top-0">
														{group}
													</div>
													{groupCats.map(cat => (
														<button
															key={cat.value}
															type="button"
															onClick={() => {
																setFormData({ ...formData, fileCategory: cat.value });
																setSearchTerm('');
																setShowCategoryDropdown(false);
															}}
															className={`w-full text-left px-4 py-2 text-sm hover:bg-blue-50 transition-colors ${
																formData.fileCategory === cat.value ? 'bg-blue-100 text-blue-900 font-medium' : 'text-gray-900'
															}`}
														>
															<div className="flex items-center justify-between">
																<span>{cat.label}</span>
																{formData.fileCategory === cat.value && (
																	<svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																		<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
																	</svg>
																)}
															</div>
														</button>
													))}
												</div>
											))
										)}
									</div>
								</div>
							)}
						</div>
					</div>

					{/* Description */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Description
						</label>
						<textarea
							value={formData.description}
							onChange={(e) => setFormData({ ...formData, description: e.target.value })}
							rows="3"
							className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
							placeholder="Add a description for this file..."
						/>
					</div>

					{/* Additional Tags */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Additional Tags (comma or space separated)
						</label>
						<input
							type="text"
							value={formData.tags}
							onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
							className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
							placeholder="e.g., confidential, draft, final"
						/>
						<p className="text-xs text-gray-500 mt-1">
							Note: Document type will be automatically added to tags
						</p>
					</div>

					{/* Actions */}
					<div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
						<button
							type="button"
							onClick={onClose}
							className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={loading}
							className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{loading ? 'Saving...' : 'Save Changes'}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default FileEditModal;

